import React, { useEffect, useMemo, useState, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { SG_APV3_FETCH_CAMPAIGN, SG_APV3_GET_ACTIONS } from "constants/actions";
import * as api from "constants/api";
import { formatElements } from "../../app/audit/AI/Copilot/components/parseText";

// InnerLogic brand palette (light) to keep styling on-brand
const brandColors = {
  primary: { 50: "#EBF1FD", 100: "#D5E2F9", 500: "#2D70E2", 600: "#2254AA" },
  secondary: { 50: "#EAFBF7", 100: "#D4F5ED", 500: "#27CDA7", 600: "#1D9A7D" },
  auxiliary: {
    red: { 50: "#FEF3F4", 100: "#FCDCE0", 500: "#F15062" },
    yellow: { 50: "#FFFAF1", 100: "#FFEDCF", 500: "#FEA711" }
  },
  neutral: { 200: "#F2F2F2", 500: "#DFDFDF", 600: "#B3B3B3" },
  background: { lightBlue: "#F8FAFF", white: "#FFFFFF" },
  text: { dark: "#2A3039", gray: "#666D79" }
};

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Page = styled.main`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 24px 32px;
  max-width: 1200px;
  margin: 0 auto;
  min-height: 100vh;
  animation: ${fadeUp} 260ms ease-out both;
`;

const Breadcrumbs = styled.nav`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 0 0 0;
  color: #6b7280;
`;

const Crumb = styled.span`
  font-size: 14px;
  font-weight: ${p => (p.active ? 600 : 500)};
  color: ${p => (p.active ? "#111827" : "#6b7280")};
  cursor: ${p => (p.clickable ? "pointer" : "default")};
  transition: color 0.2s ease;
  &:hover { color: ${p => (p.clickable ? "#374151" : p.active ? "#111827" : "#6b7280")}; }
`;

const Separator = styled.span`
  color: #d1d5db;
`;

const Header = styled.header`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 20px 0 12px 0;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 26px;
  font-weight: 700;
  color: #111827;
  letter-spacing: -0.02em;
  line-height: 1.2;
`;

// Local helpers to apply fade-up without inline style interpolation
const FadeUpInline = styled.span`
  animation: ${fadeUp} 220ms ease-out both;
`;

const FadeUpBlock = styled.div`
  animation: ${fadeUp} 220ms ease-out both;
`;

// Filters removed per request

const MetricGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(220px, 1fr));
  gap: 12px;
  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const MetricCard = styled.article`
  border: 1px solid #e6ecff;
  border-radius: 12px;
  background: #f9fbff;
  padding: 14px;
  display: grid;
  gap: 8px;
`;

const MetricHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MetricLabel = styled.div`
  font-size: 13px;
  color: #6b7280;
`;

const MetricValue = styled.div`
  font-weight: 800;
  font-size: 22px;
  color: #111827;
`;

const List = styled.div`
  display: grid;
  gap: 10px;
`;

const Card = styled.div`
  border: 1px solid ${brandColors.neutral[500]};
  border-radius: 12px;
  background: ${brandColors.background.white};
  padding: 12px;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 12px;
`;

const CardTitle = styled.div`
  font-weight: 700;
  color: ${brandColors.text.dark};
`;

const Subtle = styled.div`
  font-size: 13px;
  color: ${brandColors.text.gray};
`;

const Right = styled.div`
  display: grid;
  justify-items: end;
  gap: 6px;
`;

const Pill = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  border: 1px solid ${p => p.kind === 'status' ? '#a7f3d0' : '#fde68a'};
  color: ${p => p.kind === 'status' ? '#065f46' : '#92400e'};
  background: ${p => p.kind === 'status' ? '#ecfdf5' : '#fffbeb'};
`;

const MetricIcon = styled.span`
  display: inline-grid;
  place-items: center;
  width: 24px;
  height: 24px;
  color: ${brandColors.primary[600]};
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${p => Math.min(Math.max(p.value || 0, 0), 100)}%;
  background: linear-gradient(90deg, ${brandColors.primary[500]}, ${brandColors.secondary[500]});
`;

const EmptyState = styled.div`
  border: 1px dashed #e5e7eb;
  border-radius: 10px;
  padding: 18px;
  text-align: center;
  color: #6b7280;
`;

// Skeletons
const shimmer = keyframes`
  0% { background-position: -200px 0; }
  100% { background-position: calc(200px + 100%) 0; }
`;
const SkeletonBox = styled.div`
  position: relative; overflow: hidden; border-radius: 8px; background: #eef2f7; height: ${p => p.h || 12}px;
  &:after { content: ""; position: absolute; inset: 0; background: linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,.7) 50%, rgba(255,255,255,0) 100%); background-size: 200% 100%; animation: ${shimmer} 1200ms ease-in-out infinite; }
`;

// Action Card UI
const floatIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`;

const ActionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 12px;
`;

// Kanban layout
const KanbanGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
  align-items: start;
  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const KanbanColumn = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #ffffff;
  display: grid;
  grid-template-rows: auto 1fr;
  min-height: 120px;
`;

const KanbanColumnHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-bottom: 1px solid #f3f4f6;
`;

const KanbanColumnTitle = styled.div`
  font-weight: 700;
  color: #111827;
`;

const KanbanColumnCount = styled.span`
  font-size: 12px;
  color: #6b7280;
`;

const KanbanColumnBody = styled.div`
  display: grid;
  gap: 10px;
  padding: 10px;
`;

const ActionCardWrap = styled.article`
  position: relative;
  border: 1px solid ${brandColors.neutral[500]};
  border-radius: 14px;
  background: ${brandColors.background.white};
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  box-shadow: 0 0 0 rgba(0,0,0,0);
  transition: box-shadow 180ms ease, transform 180ms ease, border-color 180ms ease;
  animation: ${floatIn} 240ms ease both;
  will-change: transform;
  overflow: hidden;
  cursor: pointer;
  &:before {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: radial-gradient(1000px 300px at -10% -10%, ${brandColors.primary[50]}, transparent 60%);
    opacity: 0.5;
  }
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 24px rgba(0,0,0,0.06);
    border-color: ${brandColors.neutral[600]};
  }
`;

const ActionHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 10px;
  align-items: start;
`;

const ActionTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatusDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: ${p => p.status === 'completed' ? brandColors.secondary[500] : p.status === 'at_risk' ? brandColors.auxiliary.yellow[500] : p.status === 'in_progress' ? brandColors.primary[500] : '#9ca3af'};
  box-shadow: 0 0 0 3px ${p => p.status === 'completed' ? 'rgba(39,205,167,0.14)' : p.status === 'at_risk' ? 'rgba(254,167,17,0.16)' : p.status === 'in_progress' ? 'rgba(45,112,226,0.16)' : 'rgba(156,163,175,0.12)'};
`;

const ActionTitle = styled.div`
  font-weight: 700;
  color: ${brandColors.text.dark};
  letter-spacing: -0.01em;
`;

const AIPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  color: ${brandColors.text.dark};
  background: ${brandColors.background.lightBlue};
  border: 1px solid ${brandColors.neutral[500]};
`;

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  color: ${p => (p.status === 'completed' ? brandColors.secondary[600] : p.status === 'at_risk' ? '#92400e' : p.status === 'in_progress' ? brandColors.primary[600] : brandColors.text.gray)};
  background: ${p => (p.status === 'completed' ? brandColors.secondary[50] : p.status === 'at_risk' ? brandColors.auxiliary.yellow[50] : p.status === 'in_progress' ? brandColors.primary[50] : brandColors.neutral[200])};
  border: 1px solid ${p => (p.status === 'completed' ? brandColors.secondary[100] : p.status === 'at_risk' ? brandColors.auxiliary.yellow[100] : p.status === 'in_progress' ? brandColors.primary[100] : brandColors.neutral[500])};
`;

const ActionDescription = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${brandColors.text.gray};
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px 14px;
  align-items: center;
`;

const MetaItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${brandColors.text.gray};
`;

const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(17, 24, 39, 0.5);
  display: grid;
  place-items: center;
  z-index: 40;
`;

const ModalCard = styled.div`
  width: min(720px, 92vw);
  max-height: 82vh;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(0,0,0,0.15);
  display: grid;
  grid-template-rows: auto 1fr;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border-bottom: 1px solid #f0f1f3;
`;

const ModalTitle = styled.div`
  font-weight: 700;
  color: #111827;
`;

const ModalBody = styled.div`
  padding: 12px 14px;
  overflow: auto;
`;

const CloseButton = styled.button`
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #374151;
  border-radius: 8px;
  height: 30px;
  padding: 0 10px;
  font-weight: 600;
  cursor: pointer;
`;

const CheckinRow = styled.div`
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid #f3f4f6;
`;

const CheckinDate = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

const CheckinContent = styled.div`
  display: grid;
  gap: 6px;
`;

const CheckinStatus = styled.div`
  font-size: 12px;
  color: #374151;
  font-weight: 600;
`;

const CheckinNotes = styled.div`
  font-size: 13px;
  color: #4b5563;
`;

const CardActionRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 4px;
`;

const ViewButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  padding: 0 10px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #1f2937;
  font-weight: 600;
  font-size: 12px;
  cursor: pointer;
  &:hover { background: #f9fafb; }
`;

// Section layout
const Section = styled.section`
  margin-top: 8px;
  display: grid;
  gap: 12px;
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #111827;
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1.6fr 1fr;
  gap: 12px;
  align-items: start;
  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const SummaryCard = styled.div`
  position: relative;
  border: 1px solid ${brandColors.primary[100]};
  border-radius: 12px;
  background: ${brandColors.background.lightBlue};
  padding: 14px;
  display: grid;
  grid-template-rows: auto 1fr auto;
`;

const SummaryTitle = styled.div`
  font-weight: 800;
  color: ${brandColors.primary[600]};
  letter-spacing: -0.01em;
  font-size: 16px;
`;

const SummaryText = styled.div`
  margin-top: 6px;
  font-size: 13px;
  color: #4b5563;
`;

const TopSplit = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  align-items: stretch;
  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const SummaryActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
`;

const SummaryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SparkIconWrap = styled.span`
  display: inline-grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: transparent;
  & svg { width: 18px; height: 18px; }
`;

const SummaryContent = styled.div`
  margin-top: 6px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
    'Helvetica Neue', Arial, 'Noto Sans', 'Apple Color Emoji', 'Segoe UI Emoji',
    'Segoe UI Symbol', 'Noto Color Emoji', sans-serif !important;
  color: #1f2937;
  line-height: 1.6;
  & h1, & h2, & h3, & h4, & p, & li, & code, & pre, & table {
    font-family: inherit !important;
    color: inherit;
  }
  & h3 { font-size: 16px; font-weight: 800; letter-spacing: -0.01em; color: ${brandColors.primary[600]}; }
  & p { font-size: 14px; }
  & li { font-size: 14px; margin-bottom: 6px; }
`;

const DownloadButton = styled.button`
  height: 34px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid #2c6bed;
  background: linear-gradient(90deg, #2c6bed, #6b5cff);
  color: #fff;
  font-weight: 600;
  font-size: 12px;
  cursor: pointer;
  margin-top: 10px;
`;

const ViewReportButton = styled.button`
  height: 34px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid ${brandColors.primary[500]};
  background: #fff;
  color: ${brandColors.primary[600]};
  font-weight: 600;
  font-size: 12px;
  cursor: pointer;
`;

function CalendarIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <rect x="3" y="5" width="18" height="16" rx="2" stroke="#9ca3af" strokeWidth="2"/>
      <path d="M16 3v4M8 3v4M3 10h18" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );
}

function SparklesIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <path d="M10 2l1.6 3.6L15.2 7 11.6 8.6 10 12l-1.6-3.4L4.8 7l3.6-1.4L10 2z" fill="#6b5cff"/>
      <path d="M16 11l.8 1.8L18.6 14 16.8 14.8 16 16.6 15.2 14.8 13.4 14l1.8-1.2L16 11z" fill="#2c6bed"/>
    </svg>
  );
}

function formatDueLabel(dateStr) {
  if (!dateStr) return 'No timeframe';
  try {
    const d = new Date(dateStr);
    const now = new Date();
    const days = Math.ceil((d - now) / (1000 * 60 * 60 * 24));
    const base = d.toLocaleDateString();
    if (Number.isFinite(days)) {
      if (days > 0) return `Due ${base} (${days} day${days === 1 ? '' : 's'} left)`;
      if (days === 0) return `Due today (${base})`;
      const over = Math.abs(days);
      return `Overdue ${base} (${over} day${over === 1 ? '' : 's'})`;
    }
    return `Timeframe ${base}`;
  } catch (e) {
    return 'No Timeframe';
  }
}

function prettyStatusFromToken(token, label) {
  if (label) return String(label);
  if (!token) return 'Not started';
  const v = String(token).toLowerCase();
  if (v === 'completed') return 'Completed';
  if (v === 'at_risk') return 'At risk';
  if (v === 'in_progress') return 'In progress';
  return 'Not started';
}

function ChatIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <path d="M4 6a2 2 0 012-2h12a2 2 0 012 2v8a2 2 0 01-2 2H9l-5 4V6z" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function ActionCard({ action, checkinCount, onOpen, style }) {
  return (
    <ActionCardWrap
      style={style}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter') onOpen?.(); }}
      aria-label={`${action.title} action, view check-ins`}
    >
      <ActionHeader>
        <ActionTitleRow>
          <StatusDot status={action.status} />
          <ActionTitle>{action.title}</ActionTitle>
          {action.isAi && (
            <AIPill>
              <SparklesIcon /> AI
            </AIPill>
          )}
        </ActionTitleRow>
        <StatusPill status={action.status}>{prettyStatusFromToken(action.status, action.statusLabel)}</StatusPill>
      </ActionHeader>
      {action.description && (
        <ActionDescription>{action.description}</ActionDescription>
      )}
      <MetaRow>
        <MetaItem>
          <CalendarIcon />
          <span>{formatDueLabel(action.dueDate)}</span>
        </MetaItem>
        <MetaItem>
          <ChatIcon />
          <span>{(checkinCount ?? 0)} check-in{(checkinCount ?? 0) === 1 ? '' : 's'}</span>
        </MetaItem>
      </MetaRow>
      <CardActionRow>
        <ViewButton type="button" onClick={(e) => { e.stopPropagation(); onOpen?.(); }}>
          <ChatIcon /> View check-ins ({(checkinCount ?? 0)})
        </ViewButton>
      </CardActionRow>
    </ActionCardWrap>
  );
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const now = new Date();
  const d = new Date(dateStr);
  const ms = d - now;
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

export default function ManagerReport() {
  const { id, managerId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const loading = useSelector(s => s.action_planning_v3?.loading);
  const campaign = useSelector(s => s.action_planning_v3?.selectedCampaign);
  const actionsResp = useSelector(s => s.action_planning_v3?.actions);

  // State for manager report
  const [managerReport, setManagerReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  // Ensure campaign is loaded
  useEffect(() => {
    if (!campaign?.id || String(campaign.id) !== String(id)) {
      if (id) {
        dispatch({ type: SG_APV3_FETCH_CAMPAIGN, payload: { id } });
      }
    }
  }, [id, campaign, dispatch]);

  // Fetch manager report
  useEffect(() => {
    if (!id || !managerId) return;
    
    let isCancelled = false;
    
    async function fetchManagerReport() {
      setReportLoading(true);
      try {
        const params = new URLSearchParams();
        params.append('campaign', id);
        params.append('manager', managerId);
        
        const response = await api.APV3_GET_REPORTS(params.toString());
        
        if (!isCancelled && response?.data) {
          setManagerReport(response.data);
          // eslint-disable-next-line no-console
          console.log('Manager report data:', response.data);
        }
      } catch (error) {
        if (!isCancelled) {
          // eslint-disable-next-line no-console
          console.error('Error fetching manager report:', error);
        }
      } finally {
        if (!isCancelled) {
          setReportLoading(false);
        }
      }
    }
    
    fetchManagerReport();
    
    return () => {
      isCancelled = true;
    };
  }, [id, managerId]);

  // Fetch actions for this manager + campaign (and filters)
  useEffect(() => {
    const params = new URLSearchParams();
    if (id) params.append("campaign", id);
    if (managerId) params.append("manager", managerId);
    dispatch({ type: SG_APV3_GET_ACTIONS, payload: params.toString() });
  }, [id, managerId, dispatch]);

  // Engagement score: temporary static percentage per request

  const actions = useMemo(() => Array.isArray(actionsResp?.results) ? actionsResp.results : [], [actionsResp]);

  // Local cache of action details -> recent_updates
  const [actionUpdatesById, setActionUpdatesById] = useState({});
  const [openActionId, setOpenActionId] = useState(null);

  const manager = useMemo(() => {
    const list = Array.isArray(campaign?.selected_managers) ? campaign.selected_managers : [];
    return list.find(m => String(m.id) === String(managerId));
  }, [campaign, managerId]);

  const titleName = useMemo(() => {
    if (!manager) return "Manager";
    const full = manager.full_name && String(manager.full_name).trim();
    if (full) return full;
    const fn = manager.first_name || "";
    const ln = manager.last_name || "";
    const name = `${fn} ${ln}`.trim();
    return name || manager.email || `ID ${manager.id}`;
  }, [manager]);

  const total = actions.length;
  const avgProgress = total > 0 ? Math.round(actions.reduce((s, a) => s + (a.progress_percentage || 0), 0) / total) : 0;
  const normalizeStatusTokenFromName = (name, progress) => {
    const v = String(name || "").toLowerCase();
    if (v === "completed") return "completed";
    if (v.includes("behind")) return "at_risk";
    if (v.includes("block")) return "at_risk";
    if (v.includes("progress")) return "in_progress";
    if (v.includes("not") || v.includes("start")) return "not_started";
    if ((progress || 0) >= 100) return "completed";
    return "in_progress";
  };
  const atRisk = actions.filter(a => normalizeStatusTokenFromName(a.status_name, a.progress_percentage) === "at_risk").length;
  const completed = actions.filter(a => normalizeStatusTokenFromName(a.status_name, a.progress_percentage) === "completed").length;
  const onTrackCount = useMemo(() => actions.filter(a => {
    const t = normalizeStatusTokenFromName(a.status_name, a.progress_percentage);
    return t === 'in_progress' || t === 'on_track';
  }).length, [actions]);

  const mockActions = useMemo(() => ([
    { id: 'm1', title: 'Clarify team priorities for Q4', description: 'Align on top three outcomes and publish to the team space.', status: 'in_progress', dueDate: new Date(Date.now() + 1000*60*60*24*7).toISOString(), isAi: true },
    { id: 'm2', title: 'Introduce weekly 15-min standups', description: 'Pilot quick standups to improve flow and reduce slack pings.', status: 'not_started', dueDate: new Date(Date.now() + 1000*60*60*24*3).toISOString(), isAi: false },
    { id: 'm3', title: 'Run feedback pulse on workload', description: 'Measure workload clarity and bottlenecks across the group.', status: 'at_risk', dueDate: new Date(Date.now() - 1000*60*60*24*2).toISOString(), isAi: true },
    { id: 'm4', title: 'Document onboarding checklist', description: 'Create a lightweight checklist and share with contacts.', status: 'in_progress', dueDate: new Date(Date.now() + 1000*60*60*24*14).toISOString(), isAi: false },
    { id: 'm5', title: 'Shadow customer support calls', description: 'Join two calls to map most common issues.', status: 'completed', dueDate: new Date(Date.now() - 1000*60*60*24*5).toISOString(), isAi: false },
    { id: 'm6', title: 'Refine role expectations', description: 'Draft role cards and review with the team.', status: 'not_started', dueDate: null, isAi: true },
  ]), []);

  const displayActions = useMemo(() => {
    const mapped = (actions || []).map(a => ({
      id: a.id,
      title: a.title || 'Untitled action',
      description: a.description || a.next_milestone || (a.metric_focus ? `Focus: ${a.metric_focus}` : ''),
      status: normalizeStatusTokenFromName(a.status_name, a.progress_percentage),
      statusLabel: a.status_name || '',
      dueDate: a.time_bound_deadline || a.next_milestone_date || null,
      isAi: Boolean(a.ai_generated || a.is_ai || a.generated_by_ai),
    }));
    return mapped;
  }, [actions]);

  const kanbanGroups = useMemo(() => {
    const groups = { onTrack: [], atRisk: [], completed: [] };
    for (const a of displayActions) {
      const token = String(a.status || '').toLowerCase();
      const label = String(a.statusLabel || '').toLowerCase();
      if (token === 'completed') {
        groups.completed.push(a);
      } else if (token === 'in_progress' || token === 'on_track' || label.includes('on track')) {
        groups.onTrack.push(a);
      } else {
        groups.atRisk.push(a);
      }
    }
    return groups;
  }, [displayActions]);

  // Derived KPIs for new cards
  const onTrackPercent = useMemo(() => total > 0 ? Math.round((kanbanGroups.onTrack.length / total) * 100) : 0, [kanbanGroups.onTrack.length, total]);
  const actionsWithUpdates = useMemo(() => displayActions.filter(a => (actionUpdatesById[a.id] || []).length > 0).length, [displayActions, actionUpdatesById]);
  const participationPercent = useMemo(() => total > 0 ? Math.round((actionsWithUpdates / total) * 100) : 0, [actionsWithUpdates, total]);
  const avgResponsivenessDays = useMemo(() => {
    const counts = displayActions.map(a => (actionUpdatesById[a.id] || []));
    const lastAges = counts.map(list => {
      if (!list.length) return null;
      const last = list[0];
      const d = last?.created_at ? new Date(last.created_at) : null;
      if (!d) return null;
      const days = (Date.now() - d.getTime()) / (1000 * 60 * 60 * 24);
      return days;
    }).filter(v => Number.isFinite(v));
    if (!lastAges.length) return null;
    const avg = lastAges.reduce((s, v) => s + v, 0) / lastAges.length;
    return Math.round(avg * 10) / 10;
  }, [displayActions, actionUpdatesById]);

  // Extract engagement score and LLM summary from report data
  const firstReport = useMemo(() => {
    if (!managerReport?.results?.length) return null;
    return managerReport.results[0];
  }, [managerReport]);

  const engagementScorePercent = useMemo(() => {
    const overallScore = firstReport?.report_data?.overall_score;
    if (overallScore != null && Number.isFinite(overallScore)) {
      return Math.round(overallScore * 10); // Convert 0-10 scale to 0-100%
    }
    return null;
  }, [firstReport]);

  const llmSummarySections = useMemo(() => {
    const sections = firstReport?.report_data?.llm_text_summary?.sections;
    if (!Array.isArray(sections) || sections.length === 0) return null;
    // Filter out recommendations section
    return sections.filter(s => {
      const title = String(s.title || '').toLowerCase();
      return !title.includes('recommend') && !title.includes('action');
    });
  }, [firstReport]);

  const shouldShowReportCards = engagementScorePercent != null && llmSummarySections != null;

  // Fetch and log status details per action id (existing demo)
  useEffect(() => {
    const controller = new AbortController();
    async function fetchDetails() {
      for (const a of actions) {
        try {
          const resp = await api.APV3_GET_ACTION_STATUS_DETAILS({ id: a.id });
          // eslint-disable-next-line no-console
          console.log("Action status details", a.id, resp.data);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.log("Action status details error", a.id, e?.response || e?.message || e);
        }
      }
    }
    if (actions && actions.length) {
      fetchDetails();
    }
    return () => controller.abort();
  }, [actions]);

  // Fetch action details including recent_updates for check-in counts and modal
  useEffect(() => {
    let isCancelled = false;
    async function loadActionDetails() {
      if (!actions || actions.length === 0) {
        setActionUpdatesById({});
        return;
      }
      try {
        const results = await Promise.allSettled(
          actions.map((a) => api.APV3_FETCH_ACTION({ id: a.id }))
        );
        if (isCancelled) return;
        const map = {};
        results.forEach((r, idx) => {
          const id = actions[idx].id;
          if (r.status === 'fulfilled') {
            const data = r.value?.data || {};
            const updates = Array.isArray(data.recent_updates) ? data.recent_updates : [];
            map[id] = updates;
          } else {
            map[id] = [];
          }
        });
        setActionUpdatesById(map);
      } catch (_) {
        if (!isCancelled) setActionUpdatesById({});
      }
    }
    loadActionDetails();
    return () => { isCancelled = true; };
  }, [actions]);

  const openModalFor = (id) => setOpenActionId(id);
  const closeModal = () => setOpenActionId(null);

  function formatDate(value) {
    if (!value) return '';
    try {
      const d = new Date(value);
      return d.toLocaleString();
    } catch (_) {
      return String(value);
    }
  }

  const baseLoading = loading || !campaign?.id || (campaign && String(campaign.id) !== String(id));
  const [skeletonHold, setSkeletonHold] = useState(false);
  const minTimeRef = useRef(0);
  const timerRef = useRef(null);
  useEffect(() => {
    if (baseLoading) {
      minTimeRef.current = Date.now() + 1000;
      setSkeletonHold(true);
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    } else {
      const remaining = minTimeRef.current - Date.now();
      if (remaining > 0) {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => { setSkeletonHold(false); timerRef.current = null; }, remaining);
      } else {
        setSkeletonHold(false);
      }
    }
    return () => { if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; } };
  }, [baseLoading]);
  const isLoadingView = baseLoading || skeletonHold;

  return (
    <Page>
      <Breadcrumbs>
        <Crumb clickable onClick={() => navigate('/leader/audit/action-plan-v3')}>Action Planning</Crumb>
        <Separator>/</Separator>
        {isLoadingView ? (
          <Crumb><span style={{ display: 'inline-block', width: 160 }}><SkeletonBox h={12} /></span></Crumb>
        ) : (
          <Crumb clickable onClick={() => navigate(`/leader/audit/action-plan-v3/campaign/${id}?tab=managers`)}>{campaign?.name || 'Campaign'}</Crumb>
        )}
        <Separator>/</Separator>
        {isLoadingView ? (
          <Crumb active><span style={{ display: 'inline-block', width: 120 }}><SkeletonBox h={14} /></span></Crumb>
        ) : (
          <Crumb active><FadeUpInline>{titleName}</FadeUpInline></Crumb>
        )}
      </Breadcrumbs>

      <Header>
        <div>
          {isLoadingView ? (
            <>
              <div style={{ width: 260, maxWidth: '70%' }}><SkeletonBox h={26} /></div>
              <div style={{ width: 200, marginTop: 8 }}><SkeletonBox h={12} /></div>
            </>
          ) : (
            <FadeUpBlock>
              <Title>{titleName}'s Action Plan</Title>
              <Subtle>{campaign?.name ? `Campaign: ${campaign.name}` : ""}</Subtle>
            </FadeUpBlock>
          )}
        </div>
        {/* moved report CTA into summary card */}
      </Header>

      <TopSplit>
        <div>
          <MetricGrid>
            {shouldShowReportCards && (
              <MetricCard>
                <MetricHeader>
                  <MetricIcon aria-hidden><svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg></MetricIcon>
                  <MetricLabel>% engagement score</MetricLabel>
                </MetricHeader>
                {isLoadingView ? <div style={{ width: 80 }}><SkeletonBox h={22} /></div> : <MetricValue>{engagementScorePercent}%</MetricValue>}
              </MetricCard>
            )}
            <MetricCard>
              <MetricHeader>
                <MetricIcon aria-hidden><svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M3 12h18M12 3v18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg></MetricIcon>
                <MetricLabel>% participation</MetricLabel>
              </MetricHeader>
              {isLoadingView ? <div style={{ width: 60 }}><SkeletonBox h={22} /></div> : <MetricValue>{participationPercent}%</MetricValue>}
              {!isLoadingView && <Subtle>{actionsWithUpdates} of {total} actions with check-ins</Subtle>}
            </MetricCard>
            <MetricCard>
              <MetricHeader>
                <MetricIcon aria-hidden><svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 8v5l3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/></svg></MetricIcon>
                <MetricLabel>avg responsiveness (days)</MetricLabel>
              </MetricHeader>
              {isLoadingView ? <div style={{ width: 70 }}><SkeletonBox h={22} /></div> : <MetricValue>{avgResponsivenessDays != null ? `${avgResponsivenessDays}d` : '—'}</MetricValue>}
            </MetricCard>
            <MetricCard>
              <MetricHeader>
                <MetricIcon aria-hidden><svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg></MetricIcon>
                <MetricLabel>% of actions on track</MetricLabel>
              </MetricHeader>
              {isLoadingView ? <div style={{ width: 70 }}><SkeletonBox h={22} /></div> : <MetricValue>{onTrackPercent}%</MetricValue>}
              {!isLoadingView && <Subtle>{kanbanGroups.onTrack.length} on track • {completed} completed • {atRisk} at risk</Subtle>}
            </MetricCard>
          </MetricGrid>
        </div>
        <div style={{ display: 'grid' }}>
          {!isLoadingView && shouldShowReportCards && llmSummarySections && (
            <SummaryCard>
              <SummaryHeader>
                <SparkIconWrap aria-hidden>
                  <SparklesIcon />
                </SparkIconWrap>
                <SummaryTitle>Overview from report</SummaryTitle>
              </SummaryHeader>
              <SummaryContent>
                {llmSummarySections.map((section, idx) => (
                  <div key={idx} style={{ marginBottom: idx < llmSummarySections.length - 1 ? '16px' : '0' }}>
                    <h3 style={{ 
                      fontSize: '15px', 
                      fontWeight: 700, 
                      color: brandColors.primary[600],
                      marginTop: 0,
                      marginBottom: '6px',
                      letterSpacing: '-0.01em'
                    }}>
                      {section.title}
                    </h3>
                    <p style={{ 
                      fontSize: '13px', 
                      color: '#4b5563',
                      margin: 0,
                      lineHeight: '1.6'
                    }}>
                      {section.summary}
                    </p>
                  </div>
                ))}
              </SummaryContent>
              <SummaryActions>
                <ViewReportButton type="button" onClick={() => {
                  try {
                    const blob = new Blob([
                      JSON.stringify({
                        manager: titleName,
                        campaign: campaign?.name || 'Campaign',
                        totals: { total, completed, atRisk, avgProgress },
                        actions: displayActions,
                        report: firstReport?.report_data || {},
                      }, null, 2)
                    ], { type: 'application/json' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `report-summary-${(campaign?.name || 'campaign').replace(/\s+/g, '-').toLowerCase()}-${String(titleName).replace(/\s+/g, '-').toLowerCase()}.json`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    URL.revokeObjectURL(url);
                  } catch (e) { /* no-op */ }
                }}>See full report</ViewReportButton>
              </SummaryActions>
            </SummaryCard>
          )}
        </div>
      </TopSplit>

      {isLoadingView && <Subtle>Loading…</Subtle>}

      {/* summary moved to TopSplit right column */}

      <Section>
        <SectionTitle>Actions</SectionTitle>
        {!isLoadingView && displayActions.length === 0 && (
          <EmptyState>No plans yet...</EmptyState>
        )}
        {!isLoadingView && displayActions.length > 0 && (
          <KanbanGrid>
            <KanbanColumn>
              <KanbanColumnHeader>
                <KanbanColumnTitle>On track</KanbanColumnTitle>
                <KanbanColumnCount>{kanbanGroups.onTrack.length}</KanbanColumnCount>
              </KanbanColumnHeader>
              <KanbanColumnBody>
                {kanbanGroups.onTrack.map((a, idx) => (
                  <ActionCard
                    key={a.id || `ontrack-${idx}`}
                    action={a}
                    checkinCount={(actionUpdatesById[a.id] || []).length}
                    onOpen={() => openModalFor(a.id)}
                    style={{ animationDelay: `${idx * 40}ms` }}
                  />
                ))}
              </KanbanColumnBody>
            </KanbanColumn>
            <KanbanColumn>
              <KanbanColumnHeader>
                <KanbanColumnTitle>At risk</KanbanColumnTitle>
                <KanbanColumnCount>{kanbanGroups.atRisk.length}</KanbanColumnCount>
              </KanbanColumnHeader>
              <KanbanColumnBody>
                {kanbanGroups.atRisk.map((a, idx) => (
                  <ActionCard
                    key={a.id || `atrisk-${idx}`}
                    action={a}
                    checkinCount={(actionUpdatesById[a.id] || []).length}
                    onOpen={() => openModalFor(a.id)}
                    style={{ animationDelay: `${idx * 40}ms` }}
                  />
                ))}
              </KanbanColumnBody>
            </KanbanColumn>
            <KanbanColumn>
              <KanbanColumnHeader>
                <KanbanColumnTitle>Completed</KanbanColumnTitle>
                <KanbanColumnCount>{kanbanGroups.completed.length}</KanbanColumnCount>
              </KanbanColumnHeader>
              <KanbanColumnBody>
                {kanbanGroups.completed.map((a, idx) => (
                  <ActionCard
                    key={a.id || `completed-${idx}`}
                    action={a}
                    checkinCount={(actionUpdatesById[a.id] || []).length}
                    onOpen={() => openModalFor(a.id)}
                    style={{ animationDelay: `${idx * 40}ms` }}
                  />
                ))}
              </KanbanColumnBody>
            </KanbanColumn>
          </KanbanGrid>
        )}
        {isLoadingView && (
          <KanbanGrid>
            {[0,1,2].map(col => (
              <KanbanColumn key={`sk-col-${col}`}>
                <KanbanColumnHeader>
                  <KanbanColumnTitle><div style={{ width: 100 }}><SkeletonBox h={16} /></div></KanbanColumnTitle>
                  <KanbanColumnCount><div style={{ width: 30 }}><SkeletonBox h={12} /></div></KanbanColumnCount>
                </KanbanColumnHeader>
                <KanbanColumnBody>
                  {Array.from({ length: 3 }).map((_, idx) => (
                    <div key={`sk-card-${col}-${idx}`} style={{ border: '1px solid #DFDFDF', borderRadius: 14, padding: 14 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '16px 1fr auto', gap: 10, alignItems: 'center' }}>
                        <div style={{ width: 10 }}><SkeletonBox h={10} /></div>
                        <div><SkeletonBox h={14} /></div>
                        <div style={{ width: 90 }}><SkeletonBox h={18} /></div>
                      </div>
                      <div style={{ marginTop: 8 }}><SkeletonBox h={12} /></div>
                      <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                        <div style={{ width: 140 }}><SkeletonBox h={12} /></div>
                        <div style={{ width: 120 }}><SkeletonBox h={12} /></div>
                      </div>
                      <div style={{ marginTop: 8, width: 160, marginLeft: 'auto' }}><SkeletonBox h={30} /></div>
                    </div>
                  ))}
                </KanbanColumnBody>
              </KanbanColumn>
            ))}
          </KanbanGrid>
        )}
      </Section>
      {openActionId && (
        <ModalBackdrop onClick={closeModal}>
          <ModalCard onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <ModalHeader>
              <ModalTitle>Check-ins</ModalTitle>
              <CloseButton type="button" onClick={closeModal}>Close</CloseButton>
            </ModalHeader>
            <ModalBody>
              {Array.isArray(actionUpdatesById[openActionId]) && actionUpdatesById[openActionId].length > 0 ? (
                actionUpdatesById[openActionId].map((u) => (
                  <CheckinRow key={u.id}>
                    <CheckinDate>{formatDate(u.created_at)}</CheckinDate>
                    <CheckinContent>
                      <CheckinStatus>{u.status_text || ''}</CheckinStatus>
                      {u.notes && <CheckinNotes>{u.notes}</CheckinNotes>}
                    </CheckinContent>
                  </CheckinRow>
                ))
              ) : (
                <Subtle>No check-ins yet.</Subtle>
              )}
            </ModalBody>
          </ModalCard>
        </ModalBackdrop>
      )}
    </Page>
  );
}


